import { Switch, Route, Redirect } from "wouter";
import { Suspense, lazy, useEffect } from "react";
import { queryClient, setToken, getToken, clearToken } from "./lib/queryClient";
import { useQuery } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { MobileOptimizer } from "@/components/MobileOptimizer";
import { useAuth } from "@/hooks/useAuth";
import { SessionRefreshModal } from "@/components/SessionRefreshModal";
import { ProjectAssistant } from "@/components/ProjectAssistant";

import Landing from "@/pages/Landing";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import NotFound from "@/pages/not-found";
import AcceptInvitation from "@/pages/AcceptInvitation";
import AcceptGuestInvitation from "@/pages/AcceptGuestInvitation";
import { PendingApproval } from "@/pages/PendingApproval";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const AIProjectWorkflow = lazy(() => import("@/pages/AIProjectWorkflow"));
const SketchWorkspace = lazy(() => import("@/pages/SketchWorkspace"));
const Equipment = lazy(() => import("@/pages/Equipment"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const SmartScope = lazy(() => import("@/pages/SmartScope"));
const ScopePreview = lazy(() => import("@/pages/ScopePreview"));
const ImageAnalysis = lazy(() => import("@/pages/ImageAnalysis"));
const VoiceNotes = lazy(() => import("@/pages/VoiceNotes"));
const Teams = lazy(() => import("@/pages/Teams"));
const Drafts = lazy(() => import("@/pages/Drafts"));
const PhotoComparison = lazy(() => import("@/pages/PhotoComparison"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const GuestProjectView = lazy(() => import("@/pages/GuestProjectView"));
const TeamProjectDemo = lazy(() => import("@/pages/TeamProjectDemo"));
const EnhancedNotesPage = lazy(() => import("@/pages/EnhancedNotesPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const ChoosePlan = lazy(() => import("@/pages/ChoosePlan"));
const Reports = lazy(() => import("@/pages/Reports"));
const Projects = lazy(() => import("@/pages/Projects"));

// Extract the JWT from the Google OAuth redirect before React renders.
//
// Read from the URL fragment (#token=...), which the server now redirects to. A
// fragment is never sent to the server, so it does not reach CloudFront/Caddy
// access logs and is not forwarded in a Referer header — unlike a query string,
// which put a 7-day credential into every log along the path.
//
// The query string is still accepted so a link issued before this change, or a
// bookmarked redirect, still completes the login.
const oauthHash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
const oauthParams = new URLSearchParams(window.location.search);
const oauthToken = oauthHash.get("token") ?? oauthParams.get("token");
if (oauthToken) {
  setToken(oauthToken);
  oauthHash.delete("token");
  oauthParams.delete("token");
  const remainingHash = oauthHash.toString();
  const cleanUrl =
    window.location.pathname +
    (oauthParams.toString() ? "?" + oauthParams.toString() : "") +
    (remainingHash ? "#" + remainingHash : "");
  window.history.replaceState({}, "", cleanUrl);
}

// Paths that don't require an auth token
const PUBLIC_PATHS = ['/', '/login', '/signup', '/verify-email', '/forgot-password', '/reset-password', '/guest/', '/accept-invitation/', '/choose-plan'];

function isPublicPath(pathname: string) {
  return PUBLIC_PATHS.some(p => pathname === p || pathname.startsWith(p));
}

function PageLoader() {
  return (
    <div className="min-h-screen bg-ios-light flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ios-blue"></div>
    </div>
  );
}

function Router() {
  // ── Async guards (bfcache & tab-switch) ─────────────────────────────────
  // Hooks must be called unconditionally, so this comes first.
  // Catches cases where the token is removed in another tab or on a
  // bfcache page restoration.
  useEffect(() => {
    const enforceAuth = () => {
      if (!getToken() && !isPublicPath(window.location.pathname)) {
        queryClient.clear();
        window.location.replace('/login');
      }
    };

    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) enforceAuth();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') enforceAuth();
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const { isAuthenticated, isLoading } = useAuth();

  const { data: settings } = useQuery<{ theme?: string }>({
    queryKey: ["/api/settings"],
    enabled: !!getToken(),
  });

  useEffect(() => {
    const theme = settings?.theme ?? 'dark';
    if (theme === 'light') {
      document.documentElement.classList.remove('dark');
    } else {
      document.documentElement.classList.add('dark');
    }
  }, [settings?.theme]);

  // ── Synchronous token guard ──────────────────────────────────────────────
  // Checked on every render (including bfcache restorations). If the token
  // is gone and we are not on a public path, redirect immediately and render
  // nothing so the dashboard never flashes on screen.
  const token = getToken();
  const currentPath = window.location.pathname;
  if (!token && !isPublicPath(currentPath)) {
    queryClient.clear();
    window.location.replace('/login');
    return null;
  }

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <SessionRefreshModal isAuthenticated={isAuthenticated} />
      {/* Mounted once for the whole authenticated session (not per-route) so
          chat state survives navigation. Detects the active project (if any)
          from the URL itself — see projectIdFromPath in ProjectAssistant.tsx. */}
      {isAuthenticated && <ProjectAssistant />}
      <Suspense fallback={null}>
        <Switch>
          <Route path="/accept-invitation/:token">
            {(params) => <AcceptInvitation token={params.token} />}
          </Route>
          <Route path="/guest/accept/:token" component={AcceptGuestInvitation} />
          <Route path="/guest/project/:token" component={GuestProjectView} />

          {!isAuthenticated ? (
            <>
              <Route path="/login" component={LoginPage} />
              <Route path="/signup" component={SignupPage} />
              <Route path="/verify-email" component={VerifyEmailPage} />
              <Route path="/forgot-password" component={ForgotPasswordPage} />
              <Route path="/reset-password" component={ResetPasswordPage} />
              <Route path="/" component={Landing} />
              <Route path="/choose-plan" component={ChoosePlan} />
              <Route path="*">
                <Redirect to="/login" />
              </Route>
            </>
          ) : (
            // Pending approval check disabled — users proceed directly after signup/OTP
            // ) : !isApproved ? (
            //   <Route path="*" component={PendingApproval} />
            <>
              <Route path="/login">
                <Redirect to="/" />
              </Route>
              <Route path="/signup">
                <Redirect to="/" />
              </Route>
              <Route path="/" component={Dashboard} />
              <Route path="/projects" component={Projects} />
              <Route path="/projects/:id" component={AIProjectWorkflow} />
              <Route path="/ai-workflow/:id" component={AIProjectWorkflow} />
              <Route path="/projects/:id/sketch" component={SketchWorkspace} />
              <Route path="/projects/:id/scope-preview" component={ScopePreview} />
              <Route path="/projects/:id/enhanced-notes" component={EnhancedNotesPage} />
              <Route path="/dashboard" component={Dashboard} />
              <Route path="/equipment" component={Equipment} />
              <Route path="/tasks" component={Tasks} />
              <Route path="/smart-scope" component={SmartScope} />
              <Route path="/image-analysis" component={ImageAnalysis} />
              <Route path="/photo-comparison" component={PhotoComparison} />
              <Route path="/voice-notes" component={VoiceNotes} />
              <Route path="/teams" component={Teams} />
              <Route path="/drafts" component={Drafts} />
              <Route path="/reports" component={Reports} />
              <Route path="/admin" component={AdminDashboard} />
              <Route path="/settings" component={SettingsPage} />
              <Route path="/pricing" component={PricingPage} />
              <Route path="/choose-plan" component={ChoosePlan} />
              <Route path="/team-demo" component={TeamProjectDemo} />
              <Route path="*" component={NotFound} />
            </>
          )}
        </Switch>
      </Suspense>
    </>
  );
}

function App() {
  return (
    // Outer boundary catches crashes in the providers themselves; the inner one
    // around <Router /> catches route/page crashes (the white-page case) while
    // leaving the Toaster mounted.
    <ErrorBoundary label="App">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <MobileOptimizer>
            <ErrorBoundary label="Page">
              <Router />
            </ErrorBoundary>
            <Toaster />
          </MobileOptimizer>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
